/**
 * Тип позиции.
 *
 * Одно поле, от которого зависит почти вся форма товара. Разделять
 * их нужно, потому что живут они по-разному:
 *
 *  — блюдо готовят по техкарте, оно списывает сырьё и продаётся;
 *  — товар покупают и продают как есть: кола, сигареты, шоколадка;
 *  — сырьё живёт только на складе и в техкартах, на кассе его нет;
 *  — заготовку делают сами из сырья, и она сама становится сырьём;
 *  — услуга не имеет остатка вовсе: кальян-сервис, аренда зала.
 *
 * Без этого разделения владелец видит в меню муку и растительное
 * масло, а на складе — «услугу подачи» с остатком минус три.
 */
export const PRODUCT_KINDS = [
  {
    key: 'DISH' as const,
    label: 'Блюдо',
    hint: 'готовится по техкарте, списывает сырьё',
    inMenu: true,
    onStock: true,
    hasRecipe: true,
    purchased: false,
  },
  {
    key: 'GOODS' as const,
    label: 'Товар',
    hint: 'покупаем и продаём как есть: напитки, снеки',
    inMenu: true,
    onStock: true,
    hasRecipe: false,
    purchased: true,
  },
  {
    key: 'RAW' as const,
    label: 'Сырьё',
    hint: 'только склад и техкарты, на кассе не продаётся',
    inMenu: false,
    onStock: true,
    hasRecipe: false,
    purchased: true,
  },
  {
    key: 'PREP' as const,
    label: 'Заготовка',
    hint: 'делаем сами из сырья: тесто, соус, бульон',
    inMenu: false,
    onStock: true,
    hasRecipe: true,
    purchased: false,
  },
  {
    key: 'SERVICE' as const,
    label: 'Услуга',
    hint: 'остатка нет: кальян-сервис, аренда зала',
    inMenu: true,
    onStock: false,
    hasRecipe: false,
    purchased: false,
  },
] as const;

export type ProductKind = typeof PRODUCT_KINDS[number]['key'];

export function kindInfo(k: ProductKind) {
  return PRODUCT_KINDS.find((x) => x.key === k) ?? PRODUCT_KINDS[0];
}

/**
 * Какие поля показывать для этого типа.
 *
 * Форма на пятнадцать полей, из которых половина не относится к делу,
 * утомляет и провоцирует ошибки: человек заполняет «цену» у сырья
 * и удивляется, почему мука появилась в меню.
 */
export function kindFields(k: ProductKind): {
  price: boolean;
  inMenu: boolean;
  stock: boolean;
  recipe: boolean;
  purchase: boolean;
  photo: boolean;
} {
  const i = kindInfo(k);
  return {
    price: i.inMenu,
    inMenu: i.inMenu,
    stock: i.onStock,
    recipe: i.hasRecipe,
    purchase: i.purchased,
    // Фото нужно там, где позицию видит гость
    photo: i.inMenu,
  };
}

/**
 * Тип по старым признакам.
 *
 * Поля isIngredient и trackStock существовали до типа, и переписывать
 * их значениями наугад нельзя: у заведения тысяча позиций, и ошибка
 * в переносе тише всего оборачивается пропажей блюда из меню.
 */
export function kindFromFlags(f: {
  isIngredient?: boolean;
  trackStock?: boolean;
  price?: number;
  /** есть техкарта — значит блюдо готовят, а не покупают готовым */
  hasRecipe?: boolean;
}): ProductKind {
  // Сырьё помечено прямо — оно на кассе не продаётся
  if (f.isIngredient) return 'RAW';

  // Без цены продавать нечего: это заготовка или сырьё
  if ((f.price ?? 0) <= 0) return 'RAW';

  // Есть техкарта — блюдо, тут и думать нечего
  if (f.hasRecipe) return 'DISH';

  /*
   * Позиция с ценой — БЛЮДО по умолчанию, а не услуга.
   *
   * Раньше отсутствие складского учёта делало её услугой — и всё меню
   * кафе превращалось в «услуги»: у кафе учёт по умолчанию выключен,
   * чтобы не пугать владельца остатками всего подряд. Плов, лагман
   * и манты получали «калькуляция не нужна», техкарту задать было
   * нельзя, сырьё не списывалось.
   *
   * Услуг в заведении единицы — кальян-сервис, аренда зала, — и такую
   * позицию владелец пометит сам. Ошибиться в сторону блюда дешевле:
   * лишняя колонка «нет техкарты» подсказывает, а не мешает.
   */
  return f.trackStock ? 'GOODS' : 'DISH';
}
